import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text, Spinner, SpinnerSize, Dropdown, IDropdownOption,
  ComboBox, IComboBoxOption, Panel, PanelType,
  mergeStyleSets, keyframes, MessageBar, MessageBarType,
} from '@fluentui/react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  CaretLeft, Clock, ArrowsClockwise, Wrench,
  ChartBar, Funnel, Table as TableIcon, TrendUp, Warning, Users,
} from '@phosphor-icons/react';
import { boroondaraPalette } from '../theme/boroondaraTheme';
import { accessService } from '../services/accessService';
import DataTable, { Column } from '../components/common/DataTable';

// ── Palette ──────────────────────────────────────────────────────────────
const p = boroondaraPalette;
const TEAL   = '#059669';
const GREEN  = '#2e873d';
const RED    = '#D32F2F';

const TEAL_PALETTE   = ['#059669','#10B981','#34D399','#6EE7B7','#A7F3D0','#047857','#065F46','#0D9488'];
const AMBER_PALETTE  = ['#D97706','#F59E0B','#FBBF24','#FCD34D','#FDE68A','#B45309','#92400E','#F97316'];
const RED_PALETTE    = ['#DC2626','#EF4444','#F87171','#FCA5A5','#FECACA','#B91C1C','#991B1B','#E11D48'];
const BLUE_PALETTE   = ['#2563EB','#3B82F6','#60A5FA','#93C5FD','#BFDBFE','#1D4ED8','#1E40AF','#7DD3FC'];
const PURPLE_PALETTE = ['#7C3AED','#8B5CF6','#A78BFA','#C4B5FD','#DDD6FE','#6D28D9','#5B21B6','#A855F7'];

const TAB_CONFIG = [
  { id: 'overview',   label: 'Asset Overview',          icon: Wrench,    color: TEAL,      palette: TEAL_PALETTE   },
  { id: 'workorders', label: 'Work Orders',              icon: ChartBar,  color: '#2563EB', palette: BLUE_PALETTE   },
  { id: 'condition',  label: 'Condition & Inspections',  icon: TrendUp,   color: '#D97706', palette: AMBER_PALETTE  },
  { id: 'defects',    label: 'Defects',                  icon: Warning,   color: '#DC2626', palette: RED_PALETTE    },
  { id: 'requests',   label: 'Customer Requests',        icon: Users,     color: '#7C3AED', palette: PURPLE_PALETTE },
];

const TAB_FILTERS: Record<string, string[]> = {
  overview:   ['assetType'],
  workorders: ['year', 'assetType', 'department'],
  condition:  ['assetType'],
  defects:    ['assetType', 'suburb'],
  requests:   ['year'],
};

const spinAnim = keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } });
const tooltipStyle = { backgroundColor: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 4, color: '#232323', fontSize: 12 };

// ── Helpers ──────────────────────────────────────────────────────────────
function fmtCurrency(value: number): string {
  if (Math.abs(value) >= 1_000_000) return '$' + (value / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1_000)     return '$' + (value / 1_000).toFixed(0) + 'K';
  return '$' + value.toLocaleString('en-AU', { maximumFractionDigits: 0 });
}
function fmtNumber(value: number): string {
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1_000)     return (value / 1_000).toFixed(0) + 'K';
  return value.toLocaleString('en-AU', { maximumFractionDigits: 0 });
}
function fmtAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1_000)     return (value / 1_000).toFixed(0) + 'K';
  return String(value);
}
function truncLabel(label: string, max = 20): string {
  return label && label.length > max ? label.slice(0, max) + '...' : (label || '');
}
function fmtTableCurrency(val: any): string {
  if (val == null) return '-';
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// ── Styles — aligned with CRM dashboard ──────────────────────────────────
const s = mergeStyleSets({
  page: {
    padding: 32, minHeight: 'calc(100vh - 52px)',
    background: '#F5F5F5', fontFamily: "'Segoe UI', system-ui, sans-serif",
    '@media (max-width: 768px)': { padding: 16 },
  } as any,
  container: { maxWidth: 1280, margin: '0 auto' },

  // Header
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    color: p.primary, fontWeight: 600, fontSize: 13, cursor: 'pointer',
    background: 'none', border: 'none', padding: 0, marginBottom: 4,
  },
  headerRow: { marginBottom: 20 },
  pageTitle: { fontSize: 26, fontWeight: 800, color: '#1A1A1A', letterSpacing: '-0.02em' },
  pageSub:   { fontSize: 13, color: '#747474', marginTop: 4, maxWidth: 720, lineHeight: '1.6' },
  timestamp: { color: '#747474', fontSize: 12, marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 },

  // Tab bar — underline style matching CRM
  tabBar: {
    display: 'flex', borderBottom: '2px solid #E1E1E1',
    marginBottom: 24, gap: 0, flexWrap: 'wrap' as const,
  },
  tabBtn: {
    padding: '10px 20px', fontSize: 13, fontWeight: 600,
    background: 'none', border: 'none', cursor: 'pointer', transition: 'all 0.15s',
    borderBottom: '2px solid transparent', marginBottom: -2,
    selectors: { ':hover': { color: '#1A1A1A' } },
  } as any,

  // Filter bar
  filterBar: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' as const,
  },
  filterLabel: {
    fontSize: 11, fontWeight: 600, color: '#747474',
    textTransform: 'uppercase' as const, letterSpacing: '0.04em',
    display: 'flex', alignItems: 'center', gap: 4,
  },

  // KPI cards — matching CRM exactly (no icon box)
  kpiRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16, marginBottom: 24,
    '@media (max-width: 768px)': { gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 },
  } as any,
  kpiCard: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  kpiLabel: {
    fontSize: 10, fontWeight: 700, color: '#9CA3AF',
    textTransform: 'uppercase' as const, letterSpacing: '0.07em', marginBottom: 6,
  },
  kpiValue: {
    fontSize: 36, fontWeight: 800, letterSpacing: '-0.03em',
    lineHeight: '1', fontVariantNumeric: 'tabular-nums', color: '#1A1A1A',
  },
  kpiHint: { fontSize: 12, color: '#9CA3AF', marginTop: 4 },

  // Section cards — matching CRM sectionCard
  twoCol: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr' },
  } as any,
  sectionCard: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14, fontWeight: 700, color: '#1A1A1A', marginBottom: 2,
    display: 'flex', alignItems: 'center', gap: 8,
  },
  sectionSub: { fontSize: 12, color: '#9CA3AF', marginBottom: 14 },

  // Table — matching CRM table style
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 12 },
  th: {
    textAlign: 'left' as const, padding: '8px 10px', fontWeight: 700,
    fontSize: 11, color: '#747474', background: '#F9FAFB',
    borderBottom: '1px solid #E1E1E1', whiteSpace: 'nowrap' as const,
    position: 'sticky' as const, top: 0, zIndex: 1,
  },
  td: { padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#232323' },
  trStriped: { background: '#FAFAFA' },

  // Misc
  refreshBtn: {
    marginLeft: 4, color: p.primary, fontSize: 12, fontWeight: 600 as any, height: 28,
    padding: '0 10px', borderRadius: 6, border: `1px solid ${p.borderSoft}`,
    background: '#FFFFFF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
    transition: 'all 0.15s ease',
    selectors: { ':hover': { background: '#F0F8F1', borderColor: GREEN }, ':disabled': { opacity: 0.6, cursor: 'default' } },
  } as any,
  spinning: {
    animationName: spinAnim, animationDuration: '1s',
    animationIterationCount: 'infinite', animationTimingFunction: 'linear', display: 'inline-block',
  },
  centerWrap: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 },
  error: { marginBottom: 20 },
  noData: { color: '#9CA3AF', fontSize: 13, textAlign: 'center' as any, padding: 40 },
});

// ── Types ─────────────────────────────────────────────────────────────────
interface KpiEntry { label: string; raw: number; suffix?: string; detail?: string }
interface SummaryData {
  kpis?: Record<string, KpiEntry>;
  charts?: Record<string, any[]>;
  table?: any[];
  filters?: { assetTypes?: string[]; years?: number[]; suburbs?: string[]; departments?: string[] };
}

// ══════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════
export default function AssetsDashboard() {
  const nav = useNavigate();

  // ── Access control ──
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess]         = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get('/me');
        if (!alive) return;
        const d = res.data;
        if (d.role === 'admin') {
          setHasAccess(true);
        } else if (Array.isArray(d.allowedDashboards)) {
          setHasAccess(d.allowedDashboards.includes('assets_infrastructure'));
        } else {
          setHasAccess((d.allowedAgents || []).includes('assets'));
        }
      } catch { /* no access */ }
      if (alive) setAccessChecked(true);
    })();
    return () => { alive = false; };
  }, []);

  // ── State ──
  const [activeTab, setActiveTab] = useState('overview');
  const [tabFilters, setTabFilters] = useState<Record<string, Record<string, string>>>({});
  const [data, setData]       = useState<SummaryData | null>(null);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [loadedAt, setLoadedAt]   = useState('');

  // ── Asset drill drawer ──
  const [assetDrill, setAssetDrill]       = useState<{ assetType: string } | null>(null);
  const [assetDrillData, setAssetDrillData] = useState<any[] | null>(null);
  const [assetDrillLoading, setAssetDrillLoading] = useState(false);

  // ── Work-order drill drawer ──
  const [woDrill, setWoDrill]         = useState<{ category: string; year: string } | null>(null);
  const [woDrillData, setWoDrillData]   = useState<any[] | null>(null);
  const [woDrillCapped, setWoDrillCapped] = useState(false);
  const [woDrillLoading, setWoDrillLoading] = useState(false);

  const currentFilters = tabFilters[activeTab] || {};
  const setFilter = (key: string, value: string) => {
    setTabFilters(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], [key]: value } }));
  };

  // ── Fetch data ──
  const fetchData = useCallback(async (tab: string, filters: Record<string, string>, isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('tab', tab);
      if (filters.assetType)  params.set('assetType', filters.assetType);
      if (filters.year)       params.set('year', filters.year);
      if (filters.suburb)     params.set('suburb', filters.suburb);
      if (filters.department) params.set('department', filters.department);
      const res = await accessService.get(`/assets-dashboard/summary?${params}`);
      setData(res.data);
      setLoadedAt(new Date().toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }));
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load assets data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (accessChecked && hasAccess) fetchData(activeTab, tabFilters[activeTab] || {});
  }, [activeTab, tabFilters, fetchData, accessChecked, hasAccess]);

  // ── Drill callbacks ──
  const openAssetDrill = useCallback(async (assetType: string) => {
    setAssetDrill({ assetType });
    setAssetDrillData(null);
    setAssetDrillLoading(true);
    try {
      const params = new URLSearchParams({ assetType });
      const res = await accessService.get(`/assets-dashboard/asset-drill?${params}`);
      setAssetDrillData(res.data.rows || []);
    } catch {
      setAssetDrillData([]);
    } finally {
      setAssetDrillLoading(false);
    }
  }, []);

  const openWoDrill = useCallback(async (category: string, year: string) => {
    setWoDrill({ category, year });
    setWoDrillData(null);
    setWoDrillCapped(false);
    setWoDrillLoading(true);
    try {
      const params = new URLSearchParams({ category, year, status: 'open' });
      const res = await accessService.get(`/assets-dashboard/work-order-drill?${params}`);
      setWoDrillData(res.data.rows || []);
      setWoDrillCapped(res.data.capped || false);
    } catch {
      setWoDrillData([]);
    } finally {
      setWoDrillLoading(false);
    }
  }, []);

  // ── Dropdown options ──
  const assetTypeOptions: IComboBoxOption[] = useMemo(() => {
    const list = data?.filters?.assetTypes || [];
    return [{ key: '', text: 'All Types' }, ...list.map(v => ({ key: v, text: v }))];
  }, [data?.filters?.assetTypes]);

  const yearOptions: IDropdownOption[] = useMemo(() => {
    const years = data?.filters?.years || [];
    return years.map(y => ({ key: String(y), text: String(y) }));
  }, [data?.filters?.years]);

  const suburbOptions: IComboBoxOption[] = useMemo(() => {
    const list = data?.filters?.suburbs || [];
    return [{ key: '', text: 'All Suburbs' }, ...list.map(v => ({ key: v, text: v }))];
  }, [data?.filters?.suburbs]);

  const departmentOptions: IComboBoxOption[] = useMemo(() => {
    const list = data?.filters?.departments || [];
    return [{ key: '', text: 'All Departments' }, ...list.map(v => ({ key: v, text: v }))];
  }, [data?.filters?.departments]);

  const activeTabConfig = TAB_CONFIG.find(t => t.id === activeTab)!;
  const tabColor   = activeTabConfig.color;
  const tabPalette = activeTabConfig.palette;
  const tabFilterKeys = TAB_FILTERS[activeTab] || [];
  const kpis   = data?.kpis   || {};
  const charts = data?.charts || {};
  const table  = data?.table  || [];

  // ── KPI value formatter ──
  const renderKpiValue = (kpi: KpiEntry) => {
    if (kpi.suffix === '%') return kpi.raw + '%';
    if (kpi.label.includes('Cost') || kpi.label.includes('Valuation') || kpi.label.includes('Repair'))
      return fmtCurrency(kpi.raw);
    if (kpi.label.includes('Avg Condition') || kpi.label.includes('Lowest Condition') || kpi.label === 'Inspection Types')
      return String(kpi.raw);
    return fmtNumber(kpi.raw);
  };

  // ── Access gate ──
  if (!accessChecked) {
    return <div className={s.page}><div className={s.centerWrap}><Spinner size={SpinnerSize.large} label="Checking access..." /></div></div>;
  }
  if (!hasAccess) {
    return (
      <div className={s.page}>
        <div className={s.container}>
          <button className={s.backBtn} onClick={() => nav('/dashboards')}><CaretLeft size={10} /> Dashboards</button>
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <Text variant="xLarge" style={{ fontWeight: 700, color: p.text, display: 'block', marginBottom: 12 }}>Access Required</Text>
            <Text variant="medium" style={{ color: p.text2 }}>
              This is a governed dashboard. Contact your administrator to request access to Assets data.
            </Text>
          </div>
        </div>
      </div>
    );
  }
  if (loading && !data) {
    return <div className={s.page}><div className={s.centerWrap}><Spinner size={SpinnerSize.large} label="Loading Assets Dashboard..." /></div></div>;
  }

  // ══════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════
  return (
    <div className={s.page}>
      <div className={s.container}>

        {/* Back + Header */}
        <button className={s.backBtn} onClick={() => nav('/dashboards')}><CaretLeft size={10} /> Dashboards</button>
        <div className={s.headerRow}>
          <Text variant="xxLarge" className={s.pageTitle}>Assets & Infrastructure</Text>
          <Text variant="medium" className={s.pageSub}>
            Asset register, condition assessments, work orders, defects and customer requests — powered by Conquest data via Fabric Warehouse.
          </Text>
          <div className={s.timestamp}>
            <Clock size={12} color="#9CA3AF" />
            Last updated: {loadedAt}
            <button className={s.refreshBtn} onClick={() => fetchData(activeTab, currentFilters, true)} disabled={refreshing}>
              <ArrowsClockwise size={12} className={refreshing ? s.spinning : undefined} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Tab bar — underline style matching CRM */}
        <div className={s.tabBar}>
          {TAB_CONFIG.map(tab => (
            <button
              key={tab.id}
              className={s.tabBtn}
              style={{
                color: activeTab === tab.id ? tab.color : '#747474',
                borderBottomColor: activeTab === tab.id ? tab.color : 'transparent',
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        {tabFilterKeys.length > 0 && (
          <div className={s.filterBar}>
            <span className={s.filterLabel}><Funnel size={12} /> Filters</span>
            {tabFilterKeys.includes('assetType') && assetTypeOptions.length > 1 && (
              <ComboBox
                placeholder="Type to search asset types..."
                selectedKey={currentFilters.assetType || ''}
                options={assetTypeOptions}
                autoComplete="on" allowFreeInput useComboBoxAsMenuWidth
                onChange={(_, opt) => { if (opt) setFilter('assetType', (opt.key as string) || ''); }}
                onPendingValueChanged={(opt, _idx, value) => { if (!value && !opt) setFilter('assetType', ''); }}
                styles={{ root: { minWidth: 240 }, input: { fontSize: 12, fontWeight: 500, color: '#232323' }, container: { border: '1px solid #E1E1E1', borderRadius: 4 } }}
              />
            )}
            {tabFilterKeys.includes('year') && yearOptions.length > 0 && (
              <Dropdown
                placeholder="Year"
                selectedKey={currentFilters.year || (yearOptions[0]?.key as string)}
                options={yearOptions}
                onChange={(_, opt) => setFilter('year', (opt?.key as string) || '')}
                styles={{ dropdown: { minWidth: 120, border: '1px solid #E1E1E1', borderRadius: 4 }, title: { fontSize: 12, fontWeight: 500, color: '#232323', borderRadius: 4 } }}
              />
            )}
            {tabFilterKeys.includes('suburb') && suburbOptions.length > 1 && (
              <ComboBox
                placeholder="Type to search suburbs..."
                selectedKey={currentFilters.suburb || ''}
                options={suburbOptions}
                autoComplete="on" allowFreeInput useComboBoxAsMenuWidth
                onChange={(_, opt) => { if (opt) setFilter('suburb', (opt.key as string) || ''); }}
                onPendingValueChanged={(opt, _idx, value) => { if (!value && !opt) setFilter('suburb', ''); }}
                styles={{ root: { minWidth: 220 }, input: { fontSize: 12, fontWeight: 500, color: '#232323' }, container: { border: '1px solid #E1E1E1', borderRadius: 4 } }}
              />
            )}
            {tabFilterKeys.includes('department') && departmentOptions.length > 1 && (
              <ComboBox
                placeholder="Type to search departments..."
                selectedKey={currentFilters.department || ''}
                options={departmentOptions}
                autoComplete="on" allowFreeInput useComboBoxAsMenuWidth
                onChange={(_, opt) => { if (opt) setFilter('department', (opt.key as string) || ''); }}
                onPendingValueChanged={(opt, _idx, value) => { if (!value && !opt) setFilter('department', ''); }}
                styles={{ root: { minWidth: 280 }, input: { fontSize: 12, fontWeight: 500, color: '#232323' }, container: { border: '1px solid #E1E1E1', borderRadius: 4 } }}
              />
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={s.error}>
            {error}
            <button className={s.refreshBtn} onClick={() => fetchData(activeTab, currentFilters)} style={{ marginLeft: 12 }}>
              <ArrowsClockwise size={12} /> Retry
            </button>
          </MessageBar>
        )}

        {/* ══ TAB 1: ASSET OVERVIEW ══ */}
        {activeTab === 'overview' && data && (
          <>
            <div className={s.kpiRow}>
              {Object.entries(kpis).map(([key, kpi]) => (
                <div key={key} className={s.kpiCard}>
                  <div className={s.kpiLabel}>{kpi.label}</div>
                  <div className={s.kpiValue}>{renderKpiValue(kpi)}</div>
                </div>
              ))}
            </div>

            <div className={s.twoCol}>
              {charts.assetsByType && charts.assetsByType.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Assets by Type</div>
                  <div className={s.sectionSub}>click a bar to see individual assets</div>
                  <ResponsiveContainer width="100%" height={380}>
                    <BarChart data={charts.assetsByType} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} />
                      <YAxis type="category" dataKey="AssetType" width={140} tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 22)} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Assets']} />
                      <Bar dataKey="AssetCount" radius={[0, 4, 4, 0]} barSize={16}
                        onClick={(entry: any) => entry?.AssetType && openAssetDrill(entry.AssetType)}
                        cursor="pointer">
                        {charts.assetsByType.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.conditionDistribution && charts.conditionDistribution.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Condition Distribution</div>
                  <div className={s.sectionSub}>All active assets by recorded condition rating</div>
                  <ResponsiveContainer width="100%" height={380}>
                    <PieChart>
                      <Pie data={charts.conditionDistribution} dataKey="AssetCount" nameKey="Condition" cx="50%" cy="50%" innerRadius={70} outerRadius={130} paddingAngle={2}>
                        {charts.conditionDistribution.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Assets']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {charts.statusBreakdown && charts.statusBreakdown.length > 0 && (
              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Asset Status Breakdown</div>
                  <div className={s.sectionSub}>All asset statuses in the register</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={charts.statusBreakdown} dataKey="AssetCount" nameKey="Status" cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={2}>
                        {charts.statusBreakdown.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Assets']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        )}

        {/* ══ TAB 2: WORK ORDERS ══ */}
        {activeTab === 'workorders' && data && (
          <>
            <div className={s.kpiRow}>
              {Object.entries(kpis).map(([key, kpi]) => (
                <div key={key} className={s.kpiCard}>
                  <div className={s.kpiLabel}>{kpi.label}</div>
                  <div className={s.kpiValue}>{renderKpiValue(kpi)}</div>
                </div>
              ))}
            </div>

            <div className={s.twoCol}>
              {charts.monthlyTrend && charts.monthlyTrend.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Work Orders by Month</div>
                  <div className={s.sectionSub}>Volume trend for selected year</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={charts.monthlyTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                      <XAxis dataKey="Month" tick={{ fontSize: 11, fill: '#484848' }} />
                      <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Work Orders']} />
                      <Line type="monotone" dataKey="WorkOrders" stroke={tabColor} strokeWidth={2} dot={{ r: 4, fill: tabColor }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.woByType && charts.woByType.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Work Orders by Type</div>
                  <div className={s.sectionSub}>Distribution across work order types</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.woByType} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                      <XAxis dataKey="Type" tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 14)} />
                      <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Work Orders']} />
                      <Bar dataKey="WorkOrders" radius={[4, 4, 0, 0]} barSize={28}>
                        {charts.woByType.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {charts.woByPriority && charts.woByPriority.length > 0 && (
              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Work Orders by Priority</div>
                  <div className={s.sectionSub}>Top 10 priority classifications</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.woByPriority} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} />
                      <YAxis type="category" dataKey="Priority" width={160} tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 24)} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Work Orders']} />
                      <Bar dataKey="WorkOrders" radius={[0, 4, 4, 0]} barSize={14}>
                        {charts.woByPriority.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {table.length > 0 && (
              <div className={s.sectionCard}>
                <div className={s.sectionTitle}><TableIcon size={15} color={tabColor} />Open Work Orders by Category</div>
                <div className={s.sectionSub}>click a category name to see individual work orders</div>
                <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th className={s.th}>Category</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Open WOs</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {table.map((row: any, i: number) => (
                        <tr key={i} className={i % 2 === 1 ? s.trStriped : undefined}>
                          <td className={s.td}>
                            <button
                              onClick={() => row.Category && openWoDrill(row.Category, currentFilters.year || String(new Date().getFullYear()))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: tabColor, fontWeight: 600, fontSize: 12, textAlign: 'left', padding: 0, textDecoration: 'underline', fontFamily: 'inherit' }}
                            >
                              {row.Category || '-'}
                            </button>
                          </td>
                          <td className={s.td} style={{ textAlign: 'right' }}>{Number(row.OpenWOs).toLocaleString()}</td>
                          <td className={s.td} style={{ textAlign: 'right' }}>{fmtTableCurrency(row.TotalCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ══ TAB 3: CONDITION & INSPECTIONS ══ */}
        {activeTab === 'condition' && data && (
          <>
            <div className={s.kpiRow}>
              {Object.entries(kpis).map(([key, kpi]) => (
                <div key={key} className={s.kpiCard}>
                  <div className={s.kpiLabel}>{kpi.label}</div>
                  <div className={s.kpiValue}>{renderKpiValue(kpi)}</div>
                  {kpi.detail && <div className={s.kpiHint}>{kpi.detail}</div>}
                </div>
              ))}
            </div>

            <div className={s.twoCol}>
              {charts.conditionByType && charts.conditionByType.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Average Condition by Asset Type</div>
                  <div className={s.sectionSub}>Lower score = worse condition (sorted worst first)</div>
                  <ResponsiveContainer width="100%" height={380}>
                    <BarChart data={charts.conditionByType} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" domain={[0, 'auto']} tick={{ fontSize: 11, fill: '#747474' }} />
                      <YAxis type="category" dataKey="AssetType" width={140} tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 22)} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number, name: string) => [value, name === 'AvgCondition' ? 'Avg Score' : name]} />
                      <Bar dataKey="AvgCondition" radius={[0, 4, 4, 0]} barSize={14}>
                        {charts.conditionByType.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.inspectionsByYear && charts.inspectionsByYear.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Inspections by Year</div>
                  <div className={s.sectionSub}>Annual inspection volume from 2018</div>
                  <ResponsiveContainer width="100%" height={380}>
                    <LineChart data={charts.inspectionsByYear} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                      <XAxis dataKey="Year" tick={{ fontSize: 11, fill: '#484848' }} />
                      <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Inspections']} />
                      <Line type="monotone" dataKey="Inspections" stroke={tabColor} strokeWidth={2} dot={{ r: 4, fill: tabColor }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {table.length > 0 && (
              <div className={s.sectionCard}>
                <div className={s.sectionTitle}><TableIcon size={15} color={tabColor} />Asset Types by Condition (Worst First)</div>
                <div className={s.sectionSub}>Sub-types with 10+ assets, sorted by average condition score ascending</div>
                <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th className={s.th}>Asset Type</th>
                        <th className={s.th}>Sub-Type</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Avg Condition</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Worst</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Assets</th>
                      </tr>
                    </thead>
                    <tbody>
                      {table.map((row: any, i: number) => {
                        const cond = Number(row.AvgCondition) || 0;
                        const condColor = cond <= 2 ? RED : cond <= 3 ? '#D97706' : GREEN;
                        return (
                          <tr key={i} className={i % 2 === 1 ? s.trStriped : undefined}>
                            <td className={s.td}>{row.AssetType || '-'}</td>
                            <td className={s.td}>{row.SubType || '-'}</td>
                            <td className={s.td} style={{ textAlign: 'right', color: condColor, fontWeight: 700 }}>{cond}</td>
                            <td className={s.td} style={{ textAlign: 'right' }}>{row.WorstCondition ?? '-'}</td>
                            <td className={s.td} style={{ textAlign: 'right' }}>{Number(row.Assets).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ══ TAB 4: DEFECTS ══ */}
        {activeTab === 'defects' && data && (
          <>
            <div className={s.kpiRow}>
              {Object.entries(kpis).map(([key, kpi]) => (
                <div key={key} className={s.kpiCard}>
                  <div className={s.kpiLabel}>{kpi.label}</div>
                  <div className={s.kpiValue}>{renderKpiValue(kpi)}</div>
                </div>
              ))}
            </div>

            <div className={s.twoCol}>
              {charts.defectsBySuburb && charts.defectsBySuburb.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Defects by Suburb</div>
                  <div className={s.sectionSub}>Top 15 suburbs by defect count</div>
                  <ResponsiveContainer width="100%" height={380}>
                    <BarChart data={charts.defectsBySuburb} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} />
                      <YAxis type="category" dataKey="Suburb" width={140} tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 22)} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Defects']} />
                      <Bar dataKey="Defects" radius={[0, 4, 4, 0]} barSize={16}>
                        {charts.defectsBySuburb.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.defectsByCondition && charts.defectsByCondition.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Defects by Condition</div>
                  <div className={s.sectionSub}>Breakdown by recorded defect condition rating</div>
                  <ResponsiveContainer width="100%" height={380}>
                    <PieChart>
                      <Pie data={charts.defectsByCondition} dataKey="Defects" nameKey="Condition" cx="50%" cy="50%" innerRadius={70} outerRadius={130} paddingAngle={2}>
                        {charts.defectsByCondition.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Defects']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {table.length > 0 && (
              <div className={s.sectionCard}>
                <div className={s.sectionTitle}><TableIcon size={15} color={tabColor} />Defects by Asset Type</div>
                <div className={s.sectionSub}>Estimated repair cost and work order linkage</div>
                <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th className={s.th}>Asset Type</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Defects</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Est. Repair Cost</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Linked WOs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {table.map((row: any, i: number) => (
                        <tr key={i} className={i % 2 === 1 ? s.trStriped : undefined}>
                          <td className={s.td}>{row.AssetType || '-'}</td>
                          <td className={s.td} style={{ textAlign: 'right' }}>{Number(row.Defects).toLocaleString()}</td>
                          <td className={s.td} style={{ textAlign: 'right' }}>{fmtTableCurrency(row.EstRepairCost)}</td>
                          <td className={s.td} style={{ textAlign: 'right' }}>{Number(row.LinkedWOs).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ══ TAB 5: CUSTOMER REQUESTS ══ */}
        {activeTab === 'requests' && data && (
          <>
            <div className={s.kpiRow}>
              {Object.entries(kpis).map(([key, kpi]) => (
                <div key={key} className={s.kpiCard}>
                  <div className={s.kpiLabel}>{kpi.label}</div>
                  <div className={s.kpiValue}>{renderKpiValue(kpi)}</div>
                  {kpi.detail && <div className={s.kpiHint}>{kpi.detail}</div>}
                </div>
              ))}
            </div>

            <div className={s.twoCol}>
              {charts.byChannel && charts.byChannel.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Requests by Channel</div>
                  <div className={s.sectionSub}>Top 10 channels by volume</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.byChannel} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                      <XAxis dataKey="Channel" tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 14)} />
                      <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Requests']} />
                      <Bar dataKey="Requests" radius={[4, 4, 0, 0]} barSize={28}>
                        {charts.byChannel.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.monthlyTrend && charts.monthlyTrend.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Monthly Request Trend</div>
                  <div className={s.sectionSub}>Volume trend for selected year</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={charts.monthlyTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                      <XAxis dataKey="Month" tick={{ fontSize: 11, fill: '#484848' }} />
                      <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Requests']} />
                      <Line type="monotone" dataKey="Requests" stroke={tabColor} strokeWidth={2} dot={{ r: 4, fill: tabColor }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {charts.byCategory && charts.byCategory.length > 0 && (
              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Top Request Categories</div>
                  <div className={s.sectionSub}>Top 10 categories by volume</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.byCategory} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} />
                      <YAxis type="category" dataKey="Category" width={160} tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 24)} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [fmtNumber(value), 'Requests']} />
                      <Bar dataKey="Requests" radius={[0, 4, 4, 0]} barSize={14}>
                        {charts.byCategory.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {table.length > 0 && (
              <div className={s.sectionCard}>
                <div className={s.sectionTitle}><TableIcon size={15} color={tabColor} />Request Categories</div>
                <div className={s.sectionSub}>Total, completed and completion rate by category</div>
                <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th className={s.th}>Category</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Total</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Completed</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Completion %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {table.map((row: any, i: number) => {
                        const rate = Number(row.CompletionRate) || 0;
                        const rateColor = rate >= 80 ? GREEN : rate >= 50 ? '#D97706' : RED;
                        return (
                          <tr key={i} className={i % 2 === 1 ? s.trStriped : undefined}>
                            <td className={s.td}>{row.Category || '-'}</td>
                            <td className={s.td} style={{ textAlign: 'right' }}>{Number(row.TotalRequests).toLocaleString()}</td>
                            <td className={s.td} style={{ textAlign: 'right' }}>{Number(row.CompletedRequests).toLocaleString()}</td>
                            <td className={s.td} style={{ textAlign: 'right', color: rateColor, fontWeight: 700 }}>{rate}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {data && !loading && !error && !data.kpis && !data.charts && !data.table && (
          <div className={s.noData}>No data available for this tab.</div>
        )}
      </div>

      {/* ── Asset Drill Panel ── */}
      <Panel
        isOpen={!!assetDrill}
        onDismiss={() => { setAssetDrill(null); setAssetDrillData(null); }}
        type={PanelType.large}
        headerText={assetDrill?.assetType || ''}
        isLightDismiss
        styles={{
          header:     { paddingTop: 20, paddingLeft: 24 },
          headerText: { fontSize: 18, fontWeight: 700, color: '#1A1A1A', letterSpacing: '-0.02em' },
          content:    { paddingTop: 0, paddingLeft: 24, paddingRight: 24 },
        }}
      >
        {assetDrillLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
            <Spinner size={SpinnerSize.large} label="Loading assets…" />
          </div>
        )}
        {!assetDrillLoading && assetDrillData && (
          <>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 14, fontStyle: 'italic' }}>
              Individual assets · sorted by condition score (worst first) · {assetDrillData.length} records
            </div>
            {assetDrillData.length === 0
              ? <div style={{ color: '#9CA3AF', fontSize: 13, paddingTop: 32, textAlign: 'center' }}>No assets found</div>
              : (
                <DataTable
                  rows={assetDrillData}
                  exportFileName={`assets-${(assetDrill?.assetType || 'detail').replace(/\s+/g, '-').toLowerCase()}`}
                  defaultSort={{ key: 'ConditionScore', dir: 'asc' }}
                  columns={[
                    { key: 'AssetId',        label: 'Asset ID',    minWidth: 100 },
                    { key: 'Description',    label: 'Description', minWidth: 220 },
                    { key: 'SubType',        label: 'Sub-Type',    minWidth: 160 },
                    { key: 'Suburb',         label: 'Suburb',      minWidth: 120 },
                    { key: 'Condition',      label: 'Condition',   minWidth: 90  },
                    { key: 'ConditionScore', label: 'Score',       minWidth: 60, align: 'right' as const,
                      render: (row: any) => {
                        const sc = Number(row.ConditionScore);
                        const col = sc <= 2 ? RED : sc <= 3 ? '#D97706' : TEAL;
                        return <span style={{ color: col, fontWeight: 700 }}>{isNaN(sc) ? '-' : sc}</span>;
                      },
                    },
                    { key: 'Valuation',      label: 'Valuation',   minWidth: 110, align: 'right' as const },
                    { key: 'OpenWorkOrders', label: 'Open WOs',    minWidth: 80,  format: 'number' as const, align: 'right' as const },
                    { key: 'DefectCount',    label: 'Defects',     minWidth: 75,  format: 'number' as const, align: 'right' as const },
                    { key: 'RetirementDate', label: 'Retirement',  minWidth: 100 },
                  ] as Column<any>[]}
                />
              )
            }
          </>
        )}
      </Panel>

      {/* ── Work-Order Drill Panel ── */}
      <Panel
        isOpen={!!woDrill}
        onDismiss={() => { setWoDrill(null); setWoDrillData(null); }}
        type={PanelType.large}
        headerText={woDrill ? `${woDrill.category} · ${woDrill.year}` : ''}
        isLightDismiss
        styles={{
          header:     { paddingTop: 20, paddingLeft: 24 },
          headerText: { fontSize: 18, fontWeight: 700, color: '#1A1A1A', letterSpacing: '-0.02em' },
          content:    { paddingTop: 0, paddingLeft: 24, paddingRight: 24 },
        }}
      >
        {woDrillLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
            <Spinner size={SpinnerSize.large} label="Loading work orders…" />
          </div>
        )}
        {!woDrillLoading && woDrillData && (
          <>
            {woDrillCapped && (
              <div style={{ fontSize: 11, color: '#E76F51', marginBottom: 8, fontStyle: 'italic' }}>
                Showing first 500 work orders · use Export to download all
              </div>
            )}
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 14, fontStyle: 'italic' }}>
              Open work orders · most recent first · {woDrillData.length} records
            </div>
            {woDrillData.length === 0
              ? <div style={{ color: '#9CA3AF', fontSize: 13, paddingTop: 32, textAlign: 'center' }}>No open work orders found</div>
              : (
                <DataTable
                  rows={woDrillData}
                  exportFileName={`work-orders-${(woDrill?.category || 'detail').replace(/\s+/g, '-').toLowerCase()}`}
                  defaultSort={{ key: 'EntryDate', dir: 'desc' }}
                  columns={[
                    { key: 'EntryDate',  label: 'Date',        minWidth: 95  },
                    { key: 'Asset',      label: 'Asset',       minWidth: 200 },
                    { key: 'AssetType',  label: 'Type',        minWidth: 130 },
                    { key: 'Suburb',     label: 'Suburb',      minWidth: 110 },
                    { key: 'Status',     label: 'Status',      minWidth: 90  },
                    { key: 'Priority',   label: 'Priority',    minWidth: 120 },
                    { key: 'Department', label: 'Department',  minWidth: 150 },
                    { key: 'Contractor', label: 'Contractor',  minWidth: 180 },
                    { key: 'ActualCost', label: 'Actual Cost', minWidth: 110, align: 'right' as const },
                    { key: 'Completed',  label: 'Done',        minWidth: 60  },
                  ] as Column<any>[]}
                />
              )
            }
          </>
        )}
      </Panel>
    </div>
  );
}
